<template>
  <div>
    <button
      type="button"
      class="btn btn-sm btn-outline-primary"
      @click="openModal"
      v-if="hasNonRequiredClaims && !hideButton"
    >
      <font-awesome-icon :icon="['far', 'sliders']" class="me-1" />
      Claims
    </button>

    <div
      class="modal fade"
      ref="setupClaimsModal"
      tabindex="-1"
      data-bs-backdrop="static"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ $t("user.permission_for") }} {{ userRef.name }}
            </h5>
            <button
              type="button"
              class="btn btn-close"
              @click="hideModal"
              aria-label="Close"
            />
          </div>

          <div
            class="modal-body"
            v-if="claims"
          >
            <ul class="list-group">
              <template
                v-for="(claim, index) in claims"
                :key="'claim-' + index"
              >
                <li
                  class="list-group-item text-left d-flex justify-content-between"
                  v-if="!claim.required"
                >
                  <div class="form-check form-switch">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="toolRef + '-' + userRef.uid + '-' + index"
                      :ref="toolRef + userRef.uid + index"
                      :checked="userClaims?.[claim.key] ? true : false"
                      @change="setClaim(claim)"
                    />
                    <label
                      class="form-check-label"
                      :for="toolRef + '-' + userRef.uid + '-' + index"
                    >
                      {{ claim.key }}
                    </label>
                  </div>
                </li>
              </template>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {Modal} from "@comlinedev/bootstrap5";
  import {
    getFirestore,
    doc,
    updateDoc,
    deleteField,
    getDoc,
    setDoc,
  } from "firebase/firestore";
  import {
    ActivityType,
    logActivity,
  } from "../../services/activityLogger";

  export default {
    name: "SetupClaims",
    props: {
      selectedTool: {
        type: String,
        default: "",
      },
      toolId: {
        type: String,
        default: "",
      },
      selectedUser: {
        type: Object,
        default: () => ({
          uid: null,
          name: null,
        }),
      },
      uid: {
        type: String,
        default: "",
      },
      name: {
        type: String,
        default: "",
      },
      claims: {
        type: [Object, Array],
        default: () => ([]),
      },
      hideButton: {
        type: Boolean,
        default: false,
      },
      toolName: {
        type: String,
        default: "",
      },
      userEmail: {
        type: String,
        default: "",
      },
    },
    computed: {
      toolRef() {
        return this.selectedTool || this.toolId;
      },
      userRef() {
        if (this.selectedUser?.uid && this.selectedUser?.name) {
          return this.selectedUser;
        }
        return {
          uid: this.uid,
          name: this.name,
        };
      },
      hasNonRequiredClaims() {
        if (!this.claims || !Array.isArray(this.claims)) return false;
        return this.claims.some(claim => !claim.required);
      },
    },
    methods: {
      async openModal() {
        this.$emit("hide-parent");
        if (!this.userClaims) {
          await this.getUserClaims();
        }
        this.$nextTick(() => {
          if (this.claimsModal) {
            this.claimsModal.show();
          } else {
            console.error("Claims modal not initialized");
          }
        });
      },
      async setClaim(claim) {
        const wasEnabled = !!this.userClaims?.[claim.key];
        const isNowEnabled = !wasEnabled;
        
        if (isNowEnabled) {
          const ref = doc(
            doc(getFirestore(), "tools", this.toolRef),
            "claims",
            this.userRef.uid
          );

          const docRef = await getDoc(ref);
          if (docRef.exists()) {
            await updateDoc(ref, {[claim.key]: claim.value})
              .then(async () => {
                this.userClaims[claim.key] = claim.value;
                
                // Log activity
                await logActivity(ActivityType.USER_CLAIM_CHANGED, {
                  targetUser: this.userEmail || this.userRef.uid,
                  targetUserName: this.userRef.name,
                  targetTool: this.toolName,
                  toolId: this.toolRef,
                  claimKey: claim.key,
                  before: { [claim.key]: null },
                  after: { [claim.key]: claim.value }
                });
              })
              .catch((err) => {
                console.error(err);
                this.$store.commit("addError", err);
              });
          } else {
            await setDoc(ref, {[claim.key]: claim.value})
              .then(async () => {
                this.userClaims[claim.key] = claim.value;
                
                // Log activity
                await logActivity(ActivityType.USER_CLAIM_CHANGED, {
                  targetUser: this.userEmail || this.userRef.uid,
                  targetUserName: this.userRef.name,
                  targetTool: this.toolName,
                  toolId: this.toolRef,
                  claimKey: claim.key,
                  before: { [claim.key]: null },
                  after: { [claim.key]: claim.value }
                });
              })
              .catch((err) => {
                console.error(err);
                this.$store.commit("addError", err);
              });
          }
        } else {
          await updateDoc(
            doc(
              doc(getFirestore(), "tools", this.toolRef),
              "claims",
              this.userRef.uid
            ),
            {[claim.key]: deleteField()}
          )
            .then(async () => {
              const oldValue = this.userClaims[claim.key];
              delete this.userClaims[claim.key];
              
              // Log activity
              await logActivity(ActivityType.USER_CLAIM_CHANGED, {
                targetUser: this.userEmail || this.userRef.uid,
                targetUserName: this.userRef.name,
                targetTool: this.toolName,
                toolId: this.toolRef,
                claimKey: claim.key,
                before: { [claim.key]: oldValue },
                after: { [claim.key]: null }
              });
            })
            .catch((err) => {
              console.error(err);
              this.$store.commit("addError", err);
            });
        }
      },
      async getUserClaims() {
        const ref = doc(
          doc(getFirestore(), "tools", this.toolRef),
          "claims",
          this.userRef.uid
        );
        const docSnap = await getDoc(ref);
        this.userClaims = docSnap.data() || {};
      },
      hideModal() {
        this.claimsModal.hide();
        this.$emit("openModalUsers");
        this.$emit("show-parent");
      },
    },
    data() {
      return {
        userClaims: null,
        claimsModal: null,
      };
    },
    watch: {
      selectedUser() {
        if (
          this.toolRef &&
          this.userRef.uid &&
          this.userRef.name
        ) {
          this.getUserClaims();
          if (this.claimsModal) {
            this.claimsModal.show();
          }
        }
      },
      uid: {
        handler() {
          if (this.uid && this.toolId) {
            this.getUserClaims();
          }
        },
        immediate: true,
      },
    },
    mounted() {
      this.$nextTick(() => {
        if (this.$refs.setupClaimsModal) {
          this.claimsModal = new Modal(this.$refs.setupClaimsModal);
        }
      });
    },
  };
</script>
